import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, PlayCircle } from "lucide-react";

/**
 * MobileStickyCTA — fixed bar at the bottom of the viewport on mobile.
 * - Hidden on sm+ (md:hidden)
 * - Appears once the user scrolls past ~80% of the hero
 * - Uses theme tokens (--t-cta-bg / --t-cta-fg) so it auto-flips with the
 *   theme toggle.
 */
export default function MobileStickyCTA({ onStart, onWatch }) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      // Show after scrolling ~500px (roughly past the hero CTAs)
      setVisible(window.scrollY > 480);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 80, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 80, opacity: 0 }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          className="md:hidden fixed bottom-0 inset-x-0 z-[60] px-3 pb-3 pt-2"
          style={{
            paddingBottom: "calc(env(safe-area-inset-bottom, 0) + 0.75rem)",
            background:
              "linear-gradient(to top, var(--t-page-bg) 60%, transparent)",
          }}
          data-testid="mobile-sticky-cta"
        >
          <div
            className="flex items-stretch gap-2 rounded-2xl p-2"
            style={{
              background: "var(--t-surface-strong)",
              border: "1px solid var(--t-surface-border)",
              boxShadow:
                "0 -8px 30px -8px var(--t-cta-glow), 0 12px 30px -10px rgba(0,0,0,0.35)",
              backdropFilter: "blur(14px)",
              WebkitBackdropFilter: "blur(14px)",
            }}
          >
            <button
              type="button"
              onClick={onWatch}
              aria-label="Watch demo"
              className="flex items-center justify-center size-11 rounded-xl shrink-0 transition-colors active:scale-95"
              style={{
                background: "var(--t-cta-secondary-bg)",
                border: "1px solid var(--t-cta-secondary-border)",
                color: "var(--t-fg)",
              }}
              data-testid="mobile-sticky-watch"
            >
              <PlayCircle className="size-5" />
            </button>
            <button
              type="button"
              onClick={onStart}
              className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-xl h-11 px-4 text-sm font-semibold transition-transform active:scale-[0.98]"
              style={{
                background: "var(--t-cta-bg)",
                color: "var(--t-cta-fg)",
                boxShadow: "0 10px 28px -10px var(--t-cta-glow)",
              }}
              data-testid="mobile-sticky-start"
            >
              Start Free Trial
              <ArrowRight className="size-4" />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
